export type SoundName = 'dice_roll' | 'token_move' | 'token_exit' | 'safe_land' | 'stretch_enter' | 'capture' | 'win' | 'win_cheer' | 'click' | 'hover' | 'select' | 'confirm' | 'back' | 'error' | 'start' | 'turn' | 'extra_turn' | 'token_home';

export class AudioManager {
  private _ctx: AudioContext | null = null;
  private _masterGain: GainNode | null = null;
  private _musicGain: GainNode | null = null;
  private _sfxGain: GainNode | null = null;
  private _enabled: boolean = true;
  private _soundEnabled: boolean = true;
  private _musicEnabled: boolean = true;
  private _samples = new Map<SoundName, AudioBuffer>();
  /** Where the audible content starts (skips silent intros). */
  private _sampleOffsets = new Map<SoundName, number>();
  private _sampleSources = new Map<SoundName, AudioBufferSourceNode>();
  /** Menu music streams via <audio> (no 6MB WebAudio decode — old phones
      start it instantly and never jank). SFX stay on WebAudio. */
  private _musicEl: HTMLAudioElement | null = null;
  private _musicStart = 2;
  private _musicEnd = 26;
  private _wantMusic = false;
  private _musicVol = 0.7;
  private _winBuffer: AudioBuffer | null = null;
  private _winSource: AudioBufferSourceNode | null = null;

  constructor() {
    try {
      const AC: typeof AudioContext | undefined =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) throw new Error('no webaudio');
      // Start suspended — browsers unlock it on the first tap (see sound.ts).
      this._ctx = new AC();
      if (this._ctx.state === 'running') void this._ctx.suspend().catch(() => {});
      this._masterGain = this._ctx.createGain();
      this._masterGain.connect(this._ctx.destination);
      this._masterGain.gain.value = 0.5;

      this._musicGain = this._ctx.createGain();
      this._musicGain.connect(this._masterGain);
      this._musicGain.gain.value = 0.7;

      this._sfxGain = this._ctx.createGain();
      this._sfxGain.connect(this._masterGain);
      this._sfxGain.gain.value = 0.8;
    } catch {
      this._enabled = false;
    }
  }

  setMusicVolume(v: number): void {
    this._musicVol = v;
    if (this._musicGain) this._musicGain.gain.value = v;
    if (this._musicEl) this._musicEl.volume = v;
  }

  setSfxVolume(v: number): void {
    if (this._sfxGain) this._sfxGain.gain.value = v;
  }

  setSoundEnabled(on: boolean): void {
    this._soundEnabled = on;
    if (this._sfxGain) this._sfxGain.gain.value = on ? 0.8 : 0;
  }

  setMusicEnabled(on: boolean): void {
    this._musicEnabled = on;
    if (this._musicGain) this._musicGain.gain.value = on ? this._musicVol : 0;
    if (this._musicEl) this._musicEl.volume = on ? this._musicVol : 0;
    // Mute pauses but keeps _wantMusic so unmuting resumes instantly.
    // (Navigation uses stopMusic(), which clears _wantMusic.)
    if (!on) {
      if (this._musicEl && !this._musicEl.paused) {
        try { this._musicEl.pause(); } catch { /* already paused */ }
      }
    } else if (this._wantMusic) {
      this.startMusicLoop(this._musicStart, this._musicEnd);
    }
  }

  get soundEnabled(): boolean { return this._soundEnabled; }
  get musicEnabled(): boolean { return this._musicEnabled; }

  /** Preload a recorded sample from /public (played via playSample). */
  async loadSample(name: SoundName, url: string, autoTrim = false): Promise<void> {
    if (!this._ctx || this._samples.has(name)) return;
    try {
      const res = await fetch(url);
      const arr = await res.arrayBuffer();
      const buffer = await this._ctx.decodeAudioData(arr);
      this._samples.set(name, buffer);
      this._sampleOffsets.set(
        name,
        autoTrim ? this.detectAudibleStart(buffer) : 0,
      );
    } catch {
      this._samples.delete(name);
    }
  }

  /**
   * Find where the recording actually gets loud — video rips often start
   * with silence, which would make short slices play nothing.
   */
  private detectAudibleStart(buffer: AudioBuffer): number {
    const data = buffer.getChannelData(0);
    const hop = Math.floor(buffer.sampleRate * 0.05); // 50ms hops
    const window = Math.floor(buffer.sampleRate * 0.25); // 250ms window
    let bestStart = 0;
    let bestEnergy = -1;
    for (let start = 0; start + window < data.length; start += hop) {
      let sum = 0;
      for (let i = start; i < start + window; i += 16) sum += data[i] * data[i];
      if (sum > bestEnergy) {
        bestEnergy = sum;
        bestStart = start;
      }
    }
    return bestStart / buffer.sampleRate;
  }

  /** Convenience for the recorded dice-roll sample. */
  loadDiceSample(url: string = './sounds/dice-roll.mp3'): Promise<void> {
    return this.loadSample('dice_roll', url);
  }

  /** Preload the menu track as a streaming <audio> element (fast on old
      phones — no full-file fetch + decodeAudioData jank). Resolves as soon
      as enough data can play; playback itself waits for startMusicLoop. */
  async loadMusic(url: string): Promise<void> {
    if (this._musicEl) return;
    try {
      const el = new Audio();
      el.src = url;
      el.preload = 'auto';
      el.loop = false; // slice loop handled via timeupdate below
      el.volume = this._musicVol;
      (el as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
      el.addEventListener('timeupdate', () => {
        if (el.duration && el.currentTime >= this._musicEnd) {
          try { el.currentTime = this._musicStart; } catch { /* seek locked */ }
        }
      });
      this._musicEl = el;
      // Don't hang boot on 2G/slow storage — SFX still work regardless.
      await Promise.race([
        new Promise<void>((resolve) => {
          const done = (): void => resolve();
          el.addEventListener('canplaythrough', done, { once: true });
          el.addEventListener('canplay', done, { once: true });
          el.addEventListener('error', done, { once: true });
          try { void el.load(); } catch { resolve(); }
        }),
        new Promise<void>((r) => setTimeout(r, 4000)),
      ]);
    } catch {
      this._musicEl = null;
    }
  }

  /**
   * Loop only the [startSec, endSec] slice of the loaded track, forever.
   * Menu-only by design — callers (index.ts) stop it before the board.
   * Safe to call before load finishes or while muted: it records intent
   * (_wantMusic) and starts as soon as possible without ever throwing.
   */
  startMusicLoop(startSec = 2, endSec = 26): void {
    this._musicStart = startSec;
    this._musicEnd = endSec;
    this._wantMusic = true;
    if (!this._musicEnabled || !this._musicEl) return;
    const el = this._musicEl;
    // Already grooving on the wanted slice — don't restart (avoids stutter).
    if (!el.paused) return;
    try {
      if (Number.isFinite(el.duration) && el.duration > 0) {
        const s = Math.max(0, Math.min(startSec, el.duration - 0.5));
        if (Math.abs(el.currentTime - s) > 0.6) el.currentTime = s;
      } else if (el.currentTime < 0.1) {
        try { el.currentTime = startSec; } catch { /* not seekable yet */ }
      }
    } catch { /* seek guards */ }
    void el.play().catch(() => {
      // Autoplay locked — sound.ts resumes on the first tap.
    });
  }

  stopMusic(): void {
    this._wantMusic = false;
    if (this._musicEl && !this._musicEl.paused) {
      try { this._musicEl.pause(); } catch { /* already paused */ }
    }
  }

  private _adDucked = false;
  private _adWasPlaying = false;

  /**
   * Pause menu music while a full-screen ad is on screen.
   * Keeps _wantMusic intact so resumeAfterAd() can restore exactly
   * what was playing. Idempotent — safe to call twice.
   */
  pauseForAd(): void {
    if (this._adDucked) return;
    this._adDucked = true;
    this._adWasPlaying = this.isMusicPlaying;
    if (this._musicEl && !this._musicEl.paused) {
      try { this._musicEl.pause(); } catch { /* already paused */ }
    }
  }

  /**
   * Call when the ad closes / fails. Pass resume=false when navigating
   * to the (silent-by-design) board — it just clears the duck flag.
   */
  resumeAfterAd(resume = true): void {
    if (!this._adDucked) return;
    this._adDucked = false;
    const shouldResume = resume && this._adWasPlaying && this._musicEnabled && this._wantMusic;
    this._adWasPlaying = false;
    if (shouldResume && this._musicEl?.paused) {
      void this._musicEl.play().catch(() => {
        // Autoplay locked — sound.ts resumes on next tap.
      });
    }
  }

  /** Win cheer — applause when someone wins, loops until stopped. */
  async loadWinMusic(url: string = './sounds/win-cheer.mp3'): Promise<void> {
    if (!this._ctx || this._winBuffer) return;
    try {
      const res = await fetch(url);
      const arr = await res.arrayBuffer();
      this._winBuffer = await this._ctx.decodeAudioData(arr);
    } catch {
      this._winBuffer = null;
    }
  }

  playWinLoop(): void {
    if (!this._enabled || !this._ctx || !this._sfxGain) return;
    if (!this._soundEnabled || !this._winBuffer || this._winSource) return;
    if (this._ctx.state === 'suspended') this._ctx.resume();
    const src = this._ctx.createBufferSource();
    src.buffer = this._winBuffer;
    src.loop = true;
    src.connect(this._sfxGain);
    src.start(this._ctx.currentTime);
    this._winSource = src;
  }

  stopWinLoop(): void {
    if (this._winSource) {
      try { this._winSource.stop(); } catch { /* already stopped */ }
      try { this._winSource.disconnect(); } catch { /* detached */ }
      this._winSource = null;
    }
    // Also stop any one-shot win sample
    const prev = this._sampleSources.get('win');
    if (prev) { try { prev.stop(); } catch { /* */ } this._sampleSources.delete('win'); }
    const cheer = this._sampleSources.get('win_cheer');
    if (cheer) { try { cheer.stop(); } catch { /* */ } this._sampleSources.delete('win_cheer'); }
  }

  /**
   * Play a preloaded recording, trimmed to `duration` seconds with a
   * short fade-out so repeated calls never click or stack up.
   */
  private playSample(name: SoundName, duration?: number, volume = 1.2, fade = 0.12): boolean {
    if (!this._enabled || !this._ctx || !this._sfxGain || !this._soundEnabled) return false;
    const buffer = this._samples.get(name);
    if (!buffer) return false;

    const prev = this._sampleSources.get(name);
    if (prev) { try { prev.stop(); } catch { /* already stopped */ } }

    const ctx = this._ctx;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    src.connect(gain);
    gain.connect(this._sfxGain);

    const now = ctx.currentTime;
    const dur = Math.min(duration ?? buffer.duration, buffer.duration);
    const offset = Math.min(
      this._sampleOffsets.get(name) ?? 0,
      Math.max(0, buffer.duration - dur),
    );
    const fadeStart = Math.max(0, dur - fade);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.setValueAtTime(volume, now + fadeStart);
    gain.gain.linearRampToValueAtTime(0.0001, now + dur);
    src.start(now, offset, dur);
    src.stop(now + dur);
    this._sampleSources.set(name, src);
    return true;
  }

  play(name: SoundName): void {
    if (!this._enabled || !this._ctx || !this._sfxGain) return;
    if (!this._soundEnabled) return;

    if (this._ctx.state === 'suspended') {
      this._ctx.resume();
    }

    const osc = this._ctx.createOscillator();
    const gain = this._ctx.createGain();
    osc.connect(gain);
    gain.connect(this._sfxGain);

    const now = this._ctx.currentTime;

    switch (name) {
      case 'dice_roll':
        // Prefer the recorded sample; fall back to the synth blip.
        if (this.playSample('dice_roll', 1, 1.5, 0.18)) break;
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(200, now);
        osc.frequency.exponentialRampToValueAtTime(400, now + 0.1);
        osc.frequency.exponentialRampToValueAtTime(150, now + 0.2);
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
        break;

      case 'token_move':
        // Recorded goti slide — short slice so per-step calls stay crisp.
        if (this.playSample('token_move', 0.22, 1.2, 0.07)) break;
        osc.type = 'sine';
        osc.frequency.setValueAtTime(500, now);
        osc.frequency.exponentialRampToValueAtTime(700, now + 0.08);
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
        break;

      case 'token_exit': {
        // Leaving the yard: bright pop + soft thump (distinct from walking).
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(320, now);
        osc.frequency.exponentialRampToValueAtTime(760, now + 0.1);
        gain.gain.setValueAtTime(0.28, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.16);
        osc.start(now);
        osc.stop(now + 0.16);

        const thump = this._ctx.createOscillator();
        const tg = this._ctx.createGain();
        thump.type = 'sine';
        thump.frequency.setValueAtTime(170, now);
        thump.frequency.exponentialRampToValueAtTime(85, now + 0.12);
        tg.gain.setValueAtTime(0.22, now);
        tg.gain.exponentialRampToValueAtTime(0.001, now + 0.13);
        thump.connect(tg);
        tg.connect(this._sfxGain!);
        thump.start(now);
        thump.stop(now + 0.13);
        break;
      }

      case 'safe_land': {
        // Landing on a star/safe cell: soft two-note chime.
        [880, 1320].forEach((freq, i) => {
          const o = this._ctx!.createOscillator();
          const g = this._ctx!.createGain();
          o.connect(g);
          g.connect(this._sfxGain!);
          o.type = 'sine';
          o.frequency.value = freq;
          const t0 = now + i * 0.07;
          g.gain.setValueAtTime(0.16, t0);
          g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
          o.start(t0);
          o.stop(t0 + 0.22);
        });
        break;
      }

      case 'stretch_enter': {
        // Entering the winning lane: deep descending whoosh.
        osc.type = 'sine';
        osc.frequency.setValueAtTime(240, now);
        osc.frequency.exponentialRampToValueAtTime(110, now + 0.28);
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.32);
        osc.start(now);
        osc.stop(now + 0.32);

        const sub = this._ctx.createOscillator();
        const sg = this._ctx.createGain();
        sub.type = 'sine';
        sub.frequency.value = 55;
        sg.gain.setValueAtTime(0.18, now);
        sg.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
        sub.connect(sg);
        sg.connect(this._sfxGain!);
        sub.start(now);
        sub.stop(now + 0.3);
        break;
      }

      case 'capture':
        osc.type = 'square';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.exponentialRampToValueAtTime(800, now + 0.15);
        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
        osc.start(now);
        osc.stop(now + 0.25);
        break;

      case 'win': {
        const notes = [523, 659, 784, 1047];
        notes.forEach((freq, i) => {
          const o = this._ctx!.createOscillator();
          const g = this._ctx!.createGain();
          o.connect(g);
          g.connect(this._sfxGain!);
          o.type = 'sine';
          o.frequency.value = freq;
          g.gain.setValueAtTime(0.3, now + i * 0.15);
          g.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.3);
          o.start(now + i * 0.15);
          o.stop(now + i * 0.15 + 0.3);
        });
        break;
      }

      case 'click': {
        // Prefer the recorded click sample; fall back to the synth blip.
        if (this.playSample('click', 0.2, 1.3, 0.05)) break;
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(950, now);
        osc.frequency.exponentialRampToValueAtTime(480, now + 0.07);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
        osc.start(now);
        osc.stop(now + 0.09);

        const o2 = this._ctx.createOscillator();
        const g2 = this._ctx.createGain();
        o2.type = 'square';
        o2.frequency.value = 1700;
        g2.gain.setValueAtTime(0.045, now);
        g2.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
        o2.connect(g2);
        g2.connect(this._sfxGain);
        o2.start(now);
        o2.stop(now + 0.03);
        break;
      }

      case 'hover': {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(1250, now);
        gain.gain.setValueAtTime(0.05, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
        osc.start(now);
        osc.stop(now + 0.05);
        break;
      }

      case 'confirm': {
        const notes = [523, 784];
        notes.forEach((freq, i) => {
          const o = this._ctx!.createOscillator();
          const g = this._ctx!.createGain();
          o.connect(g);
          g.connect(this._sfxGain!);
          o.type = 'triangle';
          o.frequency.value = freq;
          g.gain.setValueAtTime(0.18, now + i * 0.08);
          g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.18);
          o.start(now + i * 0.08);
          o.stop(now + i * 0.08 + 0.18);
        });
        break;
      }

      case 'select': {
        const notes = [660, 990];
        notes.forEach((freq, i) => {
          const o = this._ctx!.createOscillator();
          const g = this._ctx!.createGain();
          o.connect(g);
          g.connect(this._sfxGain!);
          o.type = 'triangle';
          o.frequency.value = freq;
          g.gain.setValueAtTime(0.16, now + i * 0.07);
          g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.07 + 0.12);
          o.start(now + i * 0.07);
          o.stop(now + i * 0.07 + 0.12);
        });
        break;
      }

      case 'back': {
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(520, now);
        osc.frequency.exponentialRampToValueAtTime(240, now + 0.12);
        gain.gain.setValueAtTime(0.16, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
        osc.start(now);
        osc.stop(now + 0.14);
        break;
      }

      case 'error': {
        osc.type = 'square';
        osc.frequency.setValueAtTime(220, now);
        osc.frequency.exponentialRampToValueAtTime(140, now + 0.18);
        gain.gain.setValueAtTime(0.14, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
        break;
      }

      case 'start': {
        const notes = [392, 523, 659, 784];
        notes.forEach((freq, i) => {
          const o = this._ctx!.createOscillator();
          const g = this._ctx!.createGain();
          o.connect(g);
          g.connect(this._sfxGain!);
          o.type = 'triangle';
          o.frequency.value = freq;
          g.gain.setValueAtTime(0.18, now + i * 0.09);
          g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.09 + 0.25);
          o.start(now + i * 0.09);
          o.stop(now + i * 0.09 + 0.25);
        });
        break;
      }

      case 'turn':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, now);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);
        osc.start(now);
        osc.stop(now + 0.08);
        break;

      case 'extra_turn':
        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, now);
        osc.frequency.setValueAtTime(900, now + 0.1);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
        break;

      case 'token_home': {
        // Reaching the winning hole: rich rising two-voice flourish.
        const rise = [392, 523, 784];
        rise.forEach((freq, i) => {
          const o = this._ctx!.createOscillator();
          const g = this._ctx!.createGain();
          o.connect(g);
          g.connect(this._sfxGain!);
          o.type = 'triangle';
          o.frequency.value = freq;
          const t0 = now + i * 0.08;
          g.gain.setValueAtTime(0.22, t0);
          g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.3);
          o.start(t0);
          o.stop(t0 + 0.3);
        });
        const deep = this._ctx.createOscillator();
        const dg = this._ctx.createGain();
        deep.type = 'sine';
        deep.frequency.setValueAtTime(196, now);
        dg.gain.setValueAtTime(0.2, now);
        dg.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
        deep.connect(dg);
        dg.connect(this._sfxGain!);
        deep.start(now);
        deep.stop(now + 0.35);
        break;
      }
    }
  }

  resume(): void {
    if (this._ctx && this._ctx.state === 'suspended') {
      void this._ctx.resume();
    }
    // Autoplay was locked at boot: the first tap resumes pending menu music.
    if (this._wantMusic && this._musicEnabled && this._musicEl?.paused) {
      void this._musicEl.play().catch(() => {});
    }
  }

  /** True while the menu groove is actually audible. */
  get isMusicPlaying(): boolean {
    return !!this._musicEl && !this._musicEl.paused;
  }

  /** Halt everything (music + win + any ringing sfx) — used when app is hidden. */
  suspend(): void {
    this.stopMusic();
    this.stopWinLoop();
    if (this._ctx && this._ctx.state === 'running') {
      this._ctx.suspend();
    }
  }

  dispose(): void {
    if (this._ctx) {
      this._ctx.close();
    }
  }
}
